import { format } from 'date-fns';
import { messageOffsetMs } from './audio-sync';
import { formatDuration } from './format';

/**
 * Whether the transcript draws its timestamps in a permanent gutter or only
 * reveals them under the cursor.
 *
 *   - `'hover'` (default) — no gutter column; the timestamp appears beside a
 *     bubble on hover, which is what every live surface has always done.
 *   - `'always'` — a fixed left column carrying every row's time, so the
 *     transcript reads as a time axis while the recording plays.
 *
 * Deliberately a union rather than a boolean: `showTimestamps={false}` would
 * read as "no timestamps at all", and the hover behaviour is not nothing.
 */
export type TimestampMode = 'hover' | 'always';

/**
 * What a gutter cell says, and what its tooltip says.
 *
 * THE ROW'S OWN TIMESTAMP, ALWAYS. For a spoken line that is the moment the
 * utterance FINISHED (see `activeMessageIdAt`); for a tool row it is the moment
 * the tool fired. Both are measured, neither is derived, and that is the whole
 * point of the column.
 *
 * It used to prefer a "line start" chained off the previous line's end, which
 * made the two agree with click-to-seek at the cost of asserting that the call
 * had no silences in it. On int `c1edd217` a 13.5s wait rendered as ~4s and a
 * 2.1s reply rendered as 10s, both pointing away from the truth — and the
 * transcript's main job is spotting dead air. Chained numbers also ran the
 * column backwards, because tool rows were labelled with their own timestamp
 * while spoken rows were labelled with a derived one, so a chip could sit above
 * a bubble carrying an earlier time. One clock for every row fixes that too.
 *
 * Click-to-seek still opens earlier than the label (`lineSeekOffsets` — it
 * cannot land inside the line, so it starts at the previous line's end). The
 * two numbers are now DIFFERENT NUMBERS MEANING DIFFERENT THINGS, and the UI
 * says so: `SilenceGapRow` draws any long wait between the rows, and the
 * bubble's tooltip names the instant clicking it seeks to.
 *
 * TWO DIFFERENT CLOCKS, chosen by whether the call has a recording anchor:
 *
 *   - Anchored — the POSITION IN THE RECORDING (`1:05`). This is the number a
 *     reviewer can act on: it matches the transport's readout, so a line's
 *     time and the scrubber agree, and reading down the column shows how the
 *     call was paced.
 *   - Unanchored — WALL-CLOCK time (`9:01:03`). Without
 *     `recordingStartedAtMs` there is no position to state; the alternative is
 *     an empty column on every call recorded before that field was populated.
 *
 * `title` is always the full wall clock including AM/PM, so the absolute time
 * is one hover away in both cases and the narrow cell never has to carry it.
 * On a spoken line it is prefixed "Ends", because a bare time next to a line
 * reads as when the line started and that is the misreading being fixed.
 *
 * Returns null when the timestamp is unparseable — the cell then renders
 * empty rather than `Invalid Date`, and the column keeps its width.
 */
export function gutterTimecode(
  timestamp: string,
  anchorIso: string | undefined,
  options: { marksEndOfSpeech?: boolean } = {}
): { label: string; title: string } | null {
  const at = new Date(timestamp);
  if (!Number.isFinite(at.getTime())) return null;

  const clock = format(at, 'h:mm:ss a');
  const title = options.marksEndOfSpeech ? `Ends ${clock}` : clock;

  const offsetMs = messageOffsetMs(timestamp, anchorIso);
  if (offsetMs === null) return { label: format(at, 'h:mm:ss'), title };

  // Clamped: a row written before the recorder came up has a negative offset
  // (documented on `messageOffsetMs`), and `-0:03` is not a position in a file.
  return { label: formatDuration(Math.max(0, offsetMs)), title };
}
